<template>
  <v-data-table
    :id="config.tableId"
    v-bind="{ ...$attrs, ...$props }"
    :items="filteredItems"
    :item-value="itemKey"
    :headers="enrichedHeaders"
    :sort-by="sortBy"
    :height="calcHeight"
    fixed-header
    return-object
    select-strategy="page"
    :page="page"
    :items-per-page="itemsPerPage"
    :loading="loading ? innerLoading || loading : false"
    :hide-default-footer="filteredItems.length <= 25"
    :items-per-page-options="pageFooter.options"
    :items-per-page-text="$t('common.itemsPerPage')"
    :loading-text="$t('common.loading')"
    :row-props="rowProps"
    hover
    @click:row="handleRowClick"
    @update:sort-by="((sortBy = $event), updateStoredSortBy())"
    @update:items-per-page="((itemsPerPage = $event), updateStoredPage())"
    @update:page="((page = $event), updateStoredPage())"
    @update:current-items="getCurrentItems($event)"
  >
    <template #top>
      <DataTableToolbar
        v-if="config.toolbarConfig"
        :pro-feature="!!config.proFeature"
        :data-table-id="config.tableId"
        :config="config.toolbarConfig"
        :table-id="config.tableId"
        :num-selected-elements="selected.length"
        :general-actions="$attrs['general-actions'] ?? true"
        :csv-data-provider="buildFilteredTableContentAsCSV"
        @update:filter="updateFilter"
        @update:search="updateSearch"
      />
      <FilterRow
        v-show="!!filter"
        :id="filterRowId"
        :mount="!!filter"
        v-bind="$attrs"
        :items="items"
        :loading="innerLoading"
        :table-id="config.tableId"
        :consider-selected="showSelect"
        :enriched-headers="enrichedHeaders"
        @collect-filter-values="collectFilterValues"
        @clear-filter="clearFilterValues"
      />
    </template>

    <template #header.data-table-select>
      <v-checkbox-btn
        v-if="$attrs['select-strategy'] !== 'single'"
        :model-value="selectionState"
        hide-details
        color="primary"
        @click="handleSelectAll"
      />
    </template>
    <template #item.data-table-select="{ item }">
      <v-checkbox-btn
        v-if="showSelect"
        :model-value="isSelected(item)"
        hide-details
        color="primary"
        @click="handleRowClick"
      />
    </template>

    <template #no-data>
      <NoData
        v-if="config.noDataConfig && !search && !filter"
        :config="config.noDataConfig"
        :disabled="config.proFeature ? isFreeAccount : false"
      />
      <span v-else-if="search || filter">
        {{ $t('common.noMatchingData') }}</span
      >
      <span v-else> {{ $t('common.noData') }}</span>
    </template>
    <!-- pass through normal slots-->
    <template v-for="(_, slotName) in $slots" #[slotName]="slotData">
      <slot :name="slotName" v-bind="slotData" />
    </template>

    <!-- toggleable appended column -->
    <template v-if="$attrs['three-dot-menu']" #item.three-dot-menu="{ item }">
      <ThreeDotMenu
        :config="config.toolbarConfig?.actionsButtonConfig ?? []"
        :item="item"
        :data-table-id="config.tableId"
      />
    </template>
  </v-data-table>
  <MultiSelectionDialog
    :show-dialog="showMultiSelectionDialog"
    :config="config"
    :selected-elements="selected"
    :headers="enrichedHeaders"
    @update:show-dialog="showMultiSelectionDialog = $event"
  >
    <!-- Pass the slots the ExtendedDataTable gets, to format the table in the dialog -->
    <template v-for="(_, slotName) in $slots" #[slotName]="slotData">
      <slot :name="slotName" v-bind="slotData" />
    </template>
  </MultiSelectionDialog>
</template>

<script lang="ts">
/* eslint-disable max-lines */
import { defineComponent } from 'vue';
import { eventBus } from '@/main';
import {
  DataTableHeader,
  DataTable,
  THREE_DOT_MENU
} from '@/components/types/DataTable';
import { filterItems, transformItemValues } from '@/util/dataTable';
import { mapActions, mapState } from 'pinia';
import { appStore } from '@/store/pinia/AppStore';
import { tableStore } from '@/store/pinia/ExtendedDataTableStore';
import { onPremises } from '@/shared/utils';
import type { SortItem } from 'vuetify/lib/components/VDataTable/composables/sort';
import { authenticationStore } from '@/store/pinia/AuthenticationStore';

export default defineComponent({
  name: 'ExtendedDataTable',
  props: {
    items: {
      type: Array as () => Record<string, unknown>[],
      required: true
    },
    config: {
      type: Object as () => DataTable,
      required: true
    },
    loading: {
      type: Boolean,
      default: null
    },
    headers: {
      type: Array as () => DataTableHeader[],
      required: true
    },
    itemKey: {
      type: String,
      required: false,
      default: 'uuid'
    },
    customHeight: {
      type: String,
      default: ''
    },
    outerElementsHeight: {
      type: Number,
      default: 0
    },
    additionalSearchHeaderKeys: {
      type: Array<string>,
      default: () => []
    },
    showSelect: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update:selected'],
  data() {
    return {
      tableHeaderAndPadding: 90, // padding 25 + tableHeader 65
      rowHeight: 52,
      filter: false,
      innerLoading: true,
      itemsPerPage: 25,
      page: 1,
      sortBy: [] as SortItem[],
      search: '',
      filterValues: {},
      THREE_DOT_MENU,
      transformedItemsForFiltering: [] as Record<string, unknown>[],
      selected: [] as Record<string, unknown>[],
      filterRowId: this.config.tableId + '-filterRow',
      pageFooter: {
        options: [
          { value: 25, title: '25' },
          { value: 50, title: '50' },
          { value: 100, title: '100' }
        ]
      },
      currentPageItems: [] as Record<string, unknown>[],
      showMultiSelectionDialog: false,
      onPremises
    };
  },

  computed: {
    ...mapState(tableStore, [
      'storeTableId',
      'storedPage',
      'storedItemsPerPage',
      'storedSortBy',
      'preSelectedSearch',
      'preSelectedFilterValues'
    ]),
    ...mapState(authenticationStore, ['isFreeAccount']),
    ...mapState(appStore, ['getContainerHeight']),
    filterRowHeight() {
      const filterRow = document.querySelector(`#${this.config.tableId} thead`);
      return filterRow ? filterRow.offsetHeight : 0;
    },
    calcHeight() {
      if (this.customHeight !== '' && this.filteredItems.length !== 0) {
        return this.customHeight;
      } else {
        const tableFooterHeight = this.filteredItems.length > 25 ? 70 : 0;
        const remainingSpaceForTableItems =
          this.getContainerHeight -
          this.tableHeaderAndPadding -
          tableFooterHeight -
          this.outerElementsHeight -
          this.filterRowHeight;

        const possibleNumberOfElements =
          remainingSpaceForTableItems / this.rowHeight;

        if (this.filteredItems.length === 0) {
          return undefined;
        }

        return possibleNumberOfElements > this.filteredItems.length
          ? undefined
          : remainingSpaceForTableItems;
      }
    },
    enrichedHeaders(): DataTableHeader[] {
      let headerCopy = [...this.headers];
      headerCopy.forEach((header) => {
        // If no sortable is set, set it to true
        if (header.sortable === undefined) header.sortable = true;
      });
      headerCopy = headerCopy.filter((header) =>
        onPremises() ? !header.excludedInOnPrem : !header.excludedInCloud
      );
      headerCopy[0].title = this.config.hideCounter
        ? headerCopy[0].title
        : headerCopy[0].title.split('(')[0].concat(` (${this.currentCount})`);
      return this.$attrs['three-dot-menu']
        ? [
            ...headerCopy,
            ...[
              {
                title: '',
                value: 'three-dot-menu',
                sortable: false,
                width: 24
              } as DataTableHeader
            ]
          ]
        : headerCopy;
    },
    currentCount() {
      return this.filteredItems.length;
    },

    filteredItems(): Record<string, unknown>[] {
      if (this.transformedItemsForFiltering.length === 0) {
        this.transformItems(this.items);
      }
      return filterItems(
        [...this.items],
        this.transformedItemsForFiltering,
        this.itemKey,
        this.search,
        this.headers,
        this.filterValues,
        this.additionalSearchHeaderKeys
      );
    },
    selectionState() {
      return (
        this.selected.length > 0 &&
        this.currentPageItems.every((item) => this.selected.includes(item))
      );
    }
  },
  watch: {
    filter(newFilter) {
      void this.$nextTick(() => {
        if (!newFilter) {
          this.filterValues = {};
        }
      });
    },
    items(newItems: Record<string, unknown>[]) {
      this.transformItems(newItems);
      this.extendPageOptions();
    },
    filteredItems() {
      // Shows loading spinner until all rows are rendered after filtering
      void this.$nextTick(() => {
        const tableBody = document.querySelector(
          `#${this.config.tableId} tbody`
        );
        if (tableBody) {
          const rows = tableBody.querySelectorAll('tr'); // Check for rendered rows
          if (this.filteredItems.length === 0) this.innerLoading = false;

          if (
            !rows[0].className.includes('loading') &&
            (rows.length === this.itemsPerPage ||
              rows.length === this.filteredItems.length ||
              this.filteredItems.length === 0)
          ) {
            this.innerLoading = false;
          }
        }
      });
    },
    selected() {
      this.$emit('update:selected', this.selected);
    }
  },
  mounted() {
    const isStoredTable = this.storeTableId === this.config.tableId;
    if (isStoredTable) {
      this.filter = Object.keys(this.preSelectedFilterValues).length > 0;
      this.filterValues = { ...this.preSelectedFilterValues };
      this.search = this.preSelectedSearch;
      this.page = this.storedPage;
      this.itemsPerPage = this.storedItemsPerPage;
    }

    this.sortBy =
      isStoredTable && this.storedSortBy.length > 0
        ? this.storedSortBy
        : ((this.$attrs['sort-by'] ?? []) as SortItem[]);

    this.moveFilterRowIntoHeader();
    eventBus.$on(
      'updateSelectedItemsOfExtendedDataTable',
      (tableId: string, selectedItems: Record<string, unknown>[]) => {
        if (tableId === this.config.tableId) this.selected = selectedItems;
      }
    );
    eventBus.$on('activateTableLoading', (tableId: string) => {
      if (tableId === this.config.tableId) this.innerLoading = true;
    });
    eventBus.$on('showMultiSelectionDialogTable', (tableId: string) => {
      if (tableId === this.config.tableId) this.showMultiSelectionDialog = true;
    });
  },
  unmounted() {
    eventBus.$off('updateSelectedItemsOfExtendedDataTable');
    eventBus.$off('activateTableLoading');
    eventBus.$off('showMultiSelectionDialogTable');
  },
  methods: {
    ...mapActions(tableStore, [
      'clearStore',
      'clearPreSelectedFilterValues',
      'setPreSelectedSearchValue',
      'setPreSelectedFilterValues',
      'setStoredPageInfo',
      'setStoredSortBy'
    ]),
    // Called on demand by the toolbar's CSV download action, not on every render.
    buildFilteredTableContentAsCSV(): string {
      const selectedHeaders = this.headers.map((header) => header.title);
      const transformedItems = transformItemValues(
        [...this.filteredItems],
        this.headers
      );
      const rows = transformedItems.map((item) =>
        this.headers.map((header) => item[header.value]).join(';')
      );

      return [selectedHeaders.join(';'), ...rows].join('\n');
    },
    extendPageOptions() {
      if (this.items.length > 100) {
        this.pageFooter.options.push({ value: 250, title: '250' });
      }
      if (this.items.length > 250) {
        this.pageFooter.options.push({ value: 500, title: '500' });
      }
    },
    updateStoredSortBy() {
      if (this.config.persistentFilter) {
        this.setStoredSortBy(this.config.tableId, this.sortBy[0]);
      }
    },
    updateStoredPage() {
      if (this.config.persistentFilter) {
        this.setStoredPageInfo(
          this.config.tableId,
          this.page,
          this.itemsPerPage
        );
      }
    },
    handleSelectAll() {
      if (this.selectionState) {
        this.selected = this.selected.filter(
          (item) =>
            !this.currentPageItems.some(
              (currentPageItem) =>
                currentPageItem[this.itemKey] === item[this.itemKey]
            )
        );
      } else {
        this.selected.push(...this.currentPageItems);
        // Convert selected to Set and then back to array to remove duplicates
        this.selected = [...new Set(this.selected)];
      }
    },
    isSelected(item: Record<string, unknown>): boolean {
      return this.selected
        .flatMap((selectedItem) => selectedItem[this.itemKey])
        .includes(item[this.itemKey]);
    },
    getCurrentItems($event) {
      this.currentPageItems = $event.map((item) => item.raw);
    },
    rowProps(data) {
      const rowProps = { class: '' };
      if (this.$attrs['custom-row-props']) {
        rowProps.class = this.$attrs['custom-row-props'](data.item);
      }
      if (
        this.showSelect &&
        this.selected
          .flatMap((item) => item[this.itemKey])
          .includes(data.item[this.itemKey])
      ) {
        rowProps.class = 'selected-row';
      }
      return rowProps;
    },
    handleRowClick(_, row) {
      if (this.showSelect) {
        this.$attrs['select-strategy'] === 'single'
          ? this.clickRowSingle(row)
          : this.clickRow(row);
      } else {
        return;
      }
    },
    clickRow(row) {
      const rowIndex = this.selected.findIndex(
        (item) => item[this.itemKey] === row.item[this.itemKey]
      );
      if (rowIndex > -1) {
        this.selected.splice(rowIndex, 1);
      } else {
        this.selected.push(row.item);
      }
      this.$emit('update:selected', this.selected);
    },
    clickRowSingle(row) {
      if (this.selected[0] === row.item) this.selected = [];
      else this.selected = [row.item];
      this.$emit('update:selected', this.selected);
    },
    transformItems(rawItems: Record<string, unknown>[]) {
      if (rawItems.length !== 0 && !this.$attrs.loading) {
        this.transformedItemsForFiltering = [];
        this.transformedItemsForFiltering = transformItemValues(
          [...rawItems],
          this.headers
        );
      }
    },
    updateFilter(filter: boolean): void {
      this.filter = filter;
      if (!filter && this.config.persistentFilter) {
        this.clearPreSelectedFilterValues();
      }
    },
    updateSearch(search: string): void {
      if (this.config.persistentFilter) {
        this.setPreSelectedSearchValue(this.config.tableId, search);
      }
      this.search = search;
    },
    clearFilterValues(): void {
      this.filterValues = {};
      if (this.config.persistentFilter) {
        this.clearPreSelectedFilterValues();
      }
    },
    collectFilterValues(header: string, values: string[]): void {
      this.filterValues = {
        ...this.filterValues,
        [header]: values
      };
      if (this.config.persistentFilter) {
        this.setPreSelectedFilterValues(this.config.tableId, header, values);
      }
    },
    moveFilterRowIntoHeader() {
      // Directly select the specific table's header using the table ID
      const explicitHeader = document.querySelector(
        `#${this.config.tableId} thead`
      );
      const meRow = document.getElementById(this.filterRowId);
      if (meRow && explicitHeader) explicitHeader.appendChild(meRow);
    }
  }
});
</script>

<style lang="scss">
@use '@/styles/colors.scss';
.selected-row {
  background: colors.$selected-row !important;
}
</style>
